import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { ErrorInvitarPareja } from '@actual-app/core/server/encryption/cofre-app';

import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import { color, radio, texto } from '#components/mobile/ui/tokens';
import { useSelector } from '#redux';

import { BotonAnularEnlace } from './BotonAnularEnlace';
import type { Permiso } from './EtiquetaPermiso';
import { enlaceInvitacion } from './invitacion';
import { LineaGavi } from './LineaGavi';
import { MarcoPantalla } from './MarcoPantalla';
import { enlaceCorreo, mensajeInvitacion } from './mensajeInvitacion';
import type { MensajeInvitacion } from './mensajeInvitacion';
import { RotuloLista } from './RotuloLista';
import { tarjetaPlana } from './tarjetaPlana';
import { useAvisar } from './useAvisar';

type Estado =
  | { tipo: 'inicio' }
  | { tipo: 'creando' }
  | { tipo: 'listo'; enlace: string }
  | { tipo: 'error'; motivo: ErrorInvitarPareja };

type PasoInvitarProps = {
  fileId: string;
  nombre: string;
  /** La llave vigente del barco (de la lista de presupuestos), si va cifrado. */
  keyId: string | null;
  onVolver: () => void;
};

/**
 * «Invitar a alguien»: se elige qué podrá hacer, se crea el enlace (un solo
 * uso, 7 días) y se manda con un mensaje que dice quién invita y a qué
 * barco. En el móvil, con la hoja de compartir; en el PC, copiándolo o por
 * correo.
 */
export function PasoInvitar({
  fileId,
  nombre,
  keyId,
  onVolver,
}: PasoInvitarProps) {
  const { t } = useTranslation();
  const avisar = useAvisar();
  const { isNarrowWidth } = useResponsive();
  const de = useSelector(state => state.user.data?.displayName ?? '');
  const [permiso, setPermiso] = useState<Permiso>('editar');
  const [estado, setEstado] = useState<Estado>({ tipo: 'inicio' });
  // «Atrás» del móvil vuelve a la tripulación, no cierra la pantalla.
  useCerrarConAtras(true, onVolver);
  const conHojaCompartir =
    isNarrowWidth && typeof navigator.share === 'function';
  const puedeElegir = estado.tipo === 'inicio' || estado.tipo === 'error';

  async function crear() {
    setEstado({ tipo: 'creando' });
    const res = await send('pareja-crear-invitacion', {
      permiso,
      fileId,
      keyId,
    });
    if (res.error || !res.codigo) {
      setEstado({ tipo: 'error', motivo: res.error ?? 'sin-conexion' });
      return;
    }
    setEstado({
      tipo: 'listo',
      enlace: enlaceInvitacion(
        window.location.origin,
        res.codigo,
        res.llaveEnlace,
      ),
    });
  }

  async function anular() {
    // Solo queda un enlace sin usar por barco: crear uno anula el anterior.
    const { enlaces, error } = await send('pareja-enlaces-sin-usar', {
      fileId,
    });
    const anulados = await Promise.all(
      (enlaces ?? []).map(e =>
        send('pareja-anular-enlace', { fileId, id: e.id }),
      ),
    );
    if (error || anulados.some(r => r.error)) {
      avisar(t('Could not do it right now. Try again later.'));
      return;
    }
    setEstado({ tipo: 'inicio' });
    avisar(t('Link cancelled'));
  }

  const mensaje =
    estado.tipo === 'listo'
      ? mensajeInvitacion(t, { de, presupuesto: nombre, enlace: estado.enlace })
      : null;

  async function compartir(mensaje: MensajeInvitacion) {
    try {
      await navigator.share({ title: mensaje.asunto, text: mensaje.texto });
    } catch {
      // Cancelado: el enlace sigue a la vista para copiarlo.
    }
  }

  async function copiar(mensaje: MensajeInvitacion) {
    try {
      await navigator.clipboard.writeText(mensaje.texto);
      avisar(t('Message copied'));
    } catch {
      // Sin portapapeles: el enlace queda a la vista para copiarlo a mano.
      avisar(t('Could not do it right now. Try again later.'));
    }
  }

  const opciones: Array<{ valor: Permiso; titulo: string; detalle: string }> = [
    {
      valor: 'editar',
      titulo: t('Sees and edits everything'),
      detalle: t('Adds expenses and shares out the money, just like you.'),
    },
    {
      valor: 'ver',
      titulo: t('Only looks'),
      detalle: t('Sees everything, but changes nothing.'),
    },
  ];

  return (
    <MarcoPantalla
      titulo={t('Invite someone')}
      subtitulo={nombre}
      onAtras={onVolver}
      pie={
        estado.tipo === 'listo' ? (
          <Boton variante="fantasma" bloque onPress={onVolver}>
            <Trans>Done</Trans>
          </Boton>
        ) : (
          <Boton
            bloque
            data-testid="invitar-crear-enlace"
            isDisabled={estado.tipo === 'creando'}
            onPress={() => void crear()}
          >
            <Trans>Create link</Trans>
          </Boton>
        )
      }
    >
      <LineaGavi>
        <Trans>
          <strong>Who shall we bring aboard?</strong> The person you choose.
        </Trans>
      </LineaGavi>
      <RotuloLista>
        <Trans>What will they be able to do?</Trans>
      </RotuloLista>
      <View
        role="group"
        aria-label={t('What will they be able to do?')}
        style={{ gap: 10 }}
      >
        {opciones.map(op => {
          const marcada = permiso === op.valor;
          return (
            <Button
              key={op.valor}
              variant="bare"
              data-testid={`invitar-permiso-${op.valor}`}
              aria-pressed={marcada}
              isDisabled={!puedeElegir}
              onPress={() => setPermiso(op.valor)}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                // El botón base centra; las opciones van a la izquierda.
                justifyContent: 'flex-start',
                gap: 12,
                padding: 14,
                borderRadius: radio.tarjeta - 4,
                textAlign: 'left',
                backgroundColor: marcada ? color.accentSoft : color.surface,
                border: `1.5px solid ${marcada ? color.accent : color.line}`,
              }}
            >
              <View
                aria-hidden
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: `2px solid ${marcada ? color.accent : color.line2}`,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {marcada && (
                  <View
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: color.accent,
                    }}
                  />
                )}
              </View>
              <View style={{ minWidth: 0 }}>
                <Text style={{ fontSize: 15.5, fontWeight: 800 }}>
                  {op.titulo}
                </Text>
                <Text style={{ ...texto.secundario, color: color.fg2 }}>
                  {op.detalle}
                </Text>
              </View>
            </Button>
          );
        })}
      </View>
      {estado.tipo === 'listo' && mensaje && (
        <View
          data-testid="invitar-enlace-listo"
          style={{
            gap: 10,
            padding: 14,
            ...tarjetaPlana,
            border: `1px solid ${color.accent}`,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              fontWeight: 800,
              color: color.accent,
            }}
          >
            <Icono nombre="check" size={20} strokeWidth={2.6} />
            <Text>
              <Trans>Link ready</Trans>
            </Text>
          </View>
          <Text
            data-testid="invitar-enlace"
            style={{
              padding: '11px 12px',
              borderRadius: 10,
              backgroundColor: color.bg,
              fontSize: 13.5,
              fontWeight: 600,
              color: color.fg2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              userSelect: 'all',
            }}
          >
            {estado.enlace}
          </Text>
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            <Trans>
              It works for 7 days and for one person only. Whoever opens it
              signs in with Google and comes aboard.
            </Trans>
          </Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            {conHojaCompartir ? (
              <>
                <Boton
                  data-testid="invitar-compartir"
                  onPress={() => void compartir(mensaje)}
                  style={{ flex: 1 }}
                >
                  <Trans>Share link</Trans>
                </Boton>
                <Boton
                  variante="fantasma"
                  data-testid="invitar-copiar"
                  onPress={() => void copiar(mensaje)}
                  style={{ flex: 1 }}
                >
                  <Trans>Copy</Trans>
                </Boton>
              </>
            ) : (
              <>
                <Boton
                  data-testid="invitar-copiar"
                  onPress={() => void copiar(mensaje)}
                  style={{ flex: 1 }}
                >
                  <Trans>Copy message</Trans>
                </Boton>
                <Boton
                  variante="fantasma"
                  data-testid="invitar-correo"
                  // Abre el programa de correo con asunto y cuerpo rellenos.
                  onPress={() => window.open(enlaceCorreo(mensaje), '_self')}
                  style={{ flex: 1 }}
                >
                  <Trans>Send by email</Trans>
                </Boton>
              </>
            )}
          </View>
          <BotonAnularEnlace
            data-testid="invitar-anular"
            alinearAlCentro
            onPress={() => void anular()}
          />
        </View>
      )}
      {estado.tipo === 'error' && (
        <Text
          data-testid="invitar-error"
          style={{ ...texto.secundario, color: color.bad }}
        >
          {textoError(t, estado.motivo)}
        </Text>
      )}
    </MarcoPantalla>
  );
}

function textoError(
  t: ReturnType<typeof useTranslation>['t'],
  motivo: ErrorInvitarPareja,
): string {
  switch (motivo) {
    case 'plan-sin-compartir':
      return t('Sharing comes with Altamar.');
    case 'sin-presupuesto-en-la-nube':
      return t('First save this budget in the cloud to be able to share it.');
    case 'sin-llave':
      return t(
        'This phone does not have the key of this budget yet. Open it once here and try again.',
      );
    default:
      return t('Could not create the link. Try again later.');
  }
}
